import type {
	MRT_ColumnFilterFnsState,
	MRT_ColumnFiltersState,
	MRT_ColumnOrderState,
	MRT_GroupingState,
	MRT_Row,
	MRT_RowSelectionState,
	MRT_SortingState,
	MRT_TableInstance,
	MRT_VisibilityState,
} from "material-react-table";
import type { Props as ViewSaveDialogProps } from "../ViewSaveDialog";
import type { Job, Contact } from "../../../models";
import type { SavedView } from "../../../utilities/types";
import type { IAirtableAttachment } from "@rogoag/airtable";

export interface Props {
	setDrawerOpenState: (open: boolean) => void;
	setDisplayedJobs: (jobs: Job[]) => void;
	setLoading: (loading: boolean) => void;
	drawerOpen: boolean;
}

export interface DashboardURLState {
	sorting: MRT_SortingState;
	grouping: MRT_GroupingState;
	columnOrder: MRT_ColumnOrderState;
	columnVisibility: MRT_VisibilityState;
	columnFilterFunctions: MRT_ColumnFilterFnsState;
	columnFilterValues: MRT_ColumnFiltersState;
}

export interface FileMenuItem {
	label: string;
	property: keyof Job["attachments"];
}

export interface DownloadMenuContext {
	itemName: string;
	jobId: string;
	allFiles: IAirtableAttachment[];
	fileItems: {
		menuItem: FileMenuItem;
		files: IAirtableAttachment[];
		rowName: string;
	}[];
	showMergedCSV: boolean;
	mergedCSVFiles: IAirtableAttachment[];
}

export interface JobsDashboardTableViewModel {
	// Table instance
	table: MRT_TableInstance<Job>;

	// Data
	jobs: Job[];
	user: Contact | undefined;
	companyOptions: { id: string; name: string }[];
	selectedCompany: { id: string; name: string } | null;
	views: SavedView[];
	selectedView: SavedView | null;
	branches: { id: string; name: string }[];

	// UI state
	isLoading: boolean;
	rowSelection: MRT_RowSelectionState;
	selectionDisplayedOnMap: MRT_RowSelectionState;
	markJobsReadyVisible: boolean;
	setMarkJobsReadyVisible: (open: boolean) => void;
	inputDialogState: ViewSaveDialogProps;
	mobileMenuElement: HTMLElement | null;
	setMobileMenuElement: (el: HTMLElement | null) => void;
	actionMenuElement: HTMLElement | null;
	setActionMenuElement: (el: HTMLElement | null) => void;
	actionMenuRow: MRT_Row<Job> | null;
	setActionMenuRow: (row: MRT_Row<Job> | null) => void;
	searchParams: URLSearchParams;

	// Computed
	hasSelections: boolean;
	hasGrouping: boolean;
	selectedJobs: Job[];
	unreadySelectedJobs: Job[];
	noViewSelected: boolean;
	noJobs: boolean;
	saveViewDisabled: boolean;
	deleteViewDisabled: boolean;
	copyLinkDisabled: boolean;
	copyLinkTooltip: string;
	viewIsReadonly: boolean;

	// Handlers
	onCompanyChanged: (company: any, userData?: Contact) => Promise<void>;
	onSavedViewChanged: (event: any, value: any) => Promise<void>;
	refreshJobs: () => Promise<void>;
	showOnMap: () => Promise<void>;
	exportCSV: () => Promise<void>;
	deleteView: () => void;
	saveCurrentView: () => Promise<void>;
	copyLinkToClipboard: () => Promise<void>;
	downloadFiles: (
		jobIds: string[],
		files: IAirtableAttachment[],
		columnName: string,
		rowName: string,
		mergeCSV?: boolean,
	) => () => Promise<void>;
	copyJobId: () => void;
	getShowMapText: () => React.ReactNode;
	getMarkReadyText: (unreadySelectedJobs: Job[]) => React.ReactNode;
	getDownloadMenuContext: () => DownloadMenuContext;
}

// #region CONSTANTS

export const DEFAULT_FILTER_FNS: MRT_ColumnFilterFnsState = {
	grower: "fuzzy",
	field: "fuzzy",
	farm: "fuzzy",
	"job status": "arrIncludesSome",
	"boundary acres": "between",
	branch: "arrIncludesSome",
	region: "arrIncludesSome",
	"field ready date": "between",
	"sample date": "between",
	"creation date": "between",
	"drop/ship date": "between",
	"test package": "arrIncludesSome",
	"boundary change?": "equals",
	"boundary approved?": "equals",
	"points change?": "equals",
	submitter: "arrIncludesSome",
	season: "arrIncludesSome",
	"approved billing?": "equals",
	"job flags": "arrIncludesSome",
	"lab sent results": "between",
	"event id": "fuzzy",
	"lab submittal id": "fuzzy",
	depth: "between",
	"sample changes": "fuzzy",
	"lab name": "fuzzy",
	"test job?": "equals",
} as const;

export const DEFAULT_FILTERS: MRT_ColumnFiltersState = [];
export const DEFAULT_GROUPING: MRT_GroupingState = ["grower"];
export const DEFAULT_COLUMN_SORT: MRT_SortingState = [];
export const DEFAULT_VIS_STATE: MRT_VisibilityState = {};
export const DEFAULT_COLUMN_ORDER: MRT_ColumnOrderState = [
	"mrt-row-select",
	"mrt-row-expand",
	"grower",
	"field",
	"farm",
	"job status",
	"boundary acres",
	"field ready date",
	"sample date",
	"creation date",
	"drop/ship date",
	"test package",
	"edits",
	"files last edited?",
	"submitter",
	"approved billing?",
	"lab sent results",
	"branch",
	"region",
	"job flags",
];

export const DEFAULT_VIEW_NAME = "Group by Season, Branch, Grower";

export const FILE_MENU_LIST: FileMenuItem[] = [
	{ label: "Boundary", property: "boundarySHP" },
	{ label: "Points", property: "pointsSHP" },
	{ label: "Sample Zones", property: "zonesSHP" },
	{ label: "Executed Points", property: "executedPointsSHP" },
	{ label: "Executed Boundary", property: "executedBoundarySHP" },
	{ label: "Executed Points + Boundary", property: "executedPointsBoundarySHP" },
	{ label: "Lab Results", property: "labResults" },
	{ label: "Lab Checkin Sheet", property: "labPDF" },
	{ label: "Sampling CSV", property: "labCSV" },
];

// #endregion
